import {
  Component, ElementRef,
  Input, OnChanges,
  OnInit, Optional, Self, SimpleChanges,
  ViewChild,
} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {debounceTime, map, startWith, tap} from 'rxjs/operators';
import {ContactTagLookupModel} from '../../api/models';
import { MatFormFieldControl, MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { UntypedFormControl, NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {PhoneNumberPipe} from '../../core/pipes/phone-number.pipe';
import {parse} from 'libphonenumber-js';
import { MatChipGrid, MatChipInputEvent, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import {LookupService} from '../../api/services/lookup.service';
import {MatBaseInputComponent} from '../mat-base-input.component';
import { MatFormFieldFix } from '../mat-form-field/mat-form-field-fix';
import { NgFor, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOptgroup, MatOption } from '@angular/material/core';

@Component({
    selector: 'app-mat-recipients-multiselect',
    template: `
    <mat-form-field appearance="outline" class="regular-form-field multiselect" [hintLabel]="hintLabel"
                    (focusin)="onFocusIn($event)"
                    (focusout)="onFocusOut($event)">
      <mat-label>{{label}}</mat-label>
      <mat-chip-grid #chipList [disabled]="disabled || submitted">
        <mat-chip-row *ngFor="let item of selectedItems" [editable]="!disabled && !submitted" [removable]="!disabled && !submitted"
                  (removed)="remove(item)">
          <div class="recipient-title">
            {{ item?.contactId || item?.tagId ? item?.name : '' }} <span class="messaging-number">{{ item?.phoneNumber | phoneNumber }}</span>
          </div>
          <mat-icon matChipRemove *ngIf="!disabled && !submitted">cancel</mat-icon>
        </mat-chip-row>

        <input type="text" #inputTrigger [placeholder]="placeholder" *ngIf="!disabled && !submitted"
               matInput [matAutocomplete]="autoComplete" [formControl]="itemControl"
               [matChipInputFor]="chipList"
               [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
               (matChipInputTokenEnd)="addRecipient($event)"
        />
      </mat-chip-grid>
      <mat-autocomplete #autoComplete="matAutocomplete" (optionSelected)="optionSelected($event)">
        <mat-optgroup *ngFor="let group of groups" [label]="group">
          <mat-option *ngFor="let item of getGroupItems(group)" [value]="item" [disabled]="item.uniqueKey === 'invalid'">
            {{ item.name }} {{ item.phoneNumber | phoneNumber }}
          </mat-option>
        </mat-optgroup>

      </mat-autocomplete>
      <mat-error *ngIf="hasErrors()">{{firstError()}}</mat-error>
    </mat-form-field>
  `,
    styleUrls: ['./mat-recipients-multiselect.component.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: MatRecipientsMultiselectComponent }],
    standalone: true,
    imports: [MatFormField, MatFormFieldFix, MatLabel, MatChipGrid, NgFor, MatChipRow, NgIf, MatIcon, MatChipRemove, MatInput, MatAutocompleteTrigger, FormsModule, MatChipInput, ReactiveFormsModule, MatAutocomplete, MatOptgroup, MatOption, MatError, PhoneNumberPipe]
})
export class MatRecipientsMultiselectComponent extends MatBaseInputComponent implements OnInit, OnChanges {

  @ViewChild('chipList') chipList: MatChipGrid;

  separatorKeysCodes: number[] = [ENTER, COMMA];
  @Input() groups = ['Contacts', 'Tags'];
  phoneNumberPipe = new PhoneNumberPipe();

  @Input() set value(value: any) {
    if (value) {
      this.selectedItems = value;
    }
    this.stateChanges.next();
  }

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    public elementRef: ElementRef<HTMLElement>,
    private lookupService: LookupService
  ) {
    super(ngControl, elementRef);
  }

  @ViewChild('inputTrigger') inputTrigger: ElementRef<HTMLInputElement>;
  itemControl = new UntypedFormControl();
  stateChanges = new Subject<void>();

  items: ContactTagLookupModel[] = [];
  selectedItems: ContactTagLookupModel[] = new Array<ContactTagLookupModel>();
  lastFilter = '';

  getGroupItems(group: string): ContactTagLookupModel[] {
    return this.items.filter(x => x.type === group);
  }

  ngOnInit() {
    this.itemControl.valueChanges.pipe(
      debounceTime(500),
      startWith<string | ContactTagLookupModel[]>(''),
      map(value => typeof value === 'string' ? value : this.lastFilter),
      map(searchText => {
        return this.filter(searchText)
          .pipe(
            tap((data) => {
              if (!!searchText) {
                const newItemText = this.phoneNumberPipe.transform(searchText, true);
                if (newItemText) {
                  const isValid = !newItemText.includes('invalid');
                  if (isValid) {
                    const phoneNumber = parse(searchText, {defaultCountry: 'US', extended: true});
                    if (phoneNumber.valid) {
                      data.splice(0, 0, {
                        phoneNumber: phoneNumber.phone as string,
                        name: '',
                        type: 'Contacts',
                        uniqueKey: `${phoneNumber.phone || ''}|Contacts||''}`,
                        communicationType: 0,
                        isOptedIn: false
                      });
                    }
                  } else {
                    data.splice(0, 0, {
                      phoneNumber: newItemText,
                      name: '',
                      type: 'Contacts',
                      uniqueKey: 'invalid',
                      communicationType: 0,
                      isOptedIn: false
                    });
                  }
                }
              }
            })
          );
      })
    ).subscribe((data => {
      data.subscribe((items) => this.items = items);
    }));
  }

  remove(item: ContactTagLookupModel): void {
    const index = this.selectedItems.findIndex(value => value.uniqueKey === item.uniqueKey);
    if (index >= 0) {
      this.selectedItems.splice(index, 1);
      this.propagateChange(this.selectedItems);
      this.clearInput();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    this.clearInput();
  }

  filter(searchText: string): Observable<ContactTagLookupModel[]> {
    return this.lookupService.lookupLookupContactsAndTags({q: searchText});
  }

  addRecipient($event: MatChipInputEvent) {
    console.log($event);

    const phoneNumber = parse($event.value, {defaultCountry: 'US', extended: true});
    if (phoneNumber.valid) {
      this.selectedItems.push({
        phoneNumber: phoneNumber.phone as string,
        name: '',
        type: 'Contacts',
        uniqueKey: `${phoneNumber.phone || ''}|Contacts||''}`,
        communicationType: 0,
        isOptedIn: false
      });
      this.propagateChange(this.selectedItems);
      this.clearInput();
    } else {
      return undefined;
    }
  }

  optionSelected($event: MatAutocompleteSelectedEvent) {
    console.log($event);
    const option: ContactTagLookupModel = $event.option.value;
    this.clearInput();

    const i = this.selectedItems.findIndex(value => value.uniqueKey === option.uniqueKey);

    if (i === -1) {
      this.selectedItems.push(option);
      this.propagateChange(this.selectedItems);
    }
  }

  hasErrors() {
    let showErrors = false;
    if (!this.formControl) {
      this.errorState = showErrors;
      return showErrors;
    }

    showErrors = (this.formControl.invalid && (this.formControl.dirty || this.formControl.touched))
      || (this.submitted && this.formControl.invalid);

    this.errorState = showErrors;
    if (this.chipList) {
      this.chipList.errorState = showErrors;
    }

    return showErrors;
  }

  firstError(): string {
    const message = super.firstError();
    if (message) {
      this.hasErrors();
    } else {
      this.errorState = false;
    }
    return message;
  }

  clearInput() {
    if (this.inputTrigger && this.inputTrigger.nativeElement) {
      this.inputTrigger.nativeElement.value = '';
    }

    this.itemControl.setValue(null);
  }
}
